import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";

// Shared admin Button with token-based visual styles for consistent admin UI
// and consistent interaction semantics.
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "destructive"
  | "success";

export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
}

// Pointer-split density: every size keeps the >=44px coarse-pointer floor
// (min-h-11 / min-w-11), while `pointer-fine:` densifies for mouse users.
// Per-size min-heights/min-widths live ONLY in sizeStyles — never add a
// `pointer-fine:min-h-*` / `pointer-fine:min-w-*` here, or two conflicting
// pointer-fine minimums could flow through cn() onto one element.
const baseStyles =
  "inline-flex items-center justify-center font-medium rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

const variantStyles: Record<ButtonVariant, string> = {
  primary: "bg-primary-600 text-primary-foreground hover:bg-primary-700 focus:ring-primary-500",
  secondary: "bg-neutral-100 text-neutral-900 hover:bg-neutral-200 focus:ring-neutral-500",
  outline:
    "border border-neutral-300 bg-surface text-foreground hover:bg-neutral-50 focus:ring-primary-500",
  ghost: "bg-transparent text-foreground hover:bg-neutral-100 focus:ring-neutral-500",
  destructive: "bg-destructive-600 text-destructive-foreground hover:bg-destructive-700 focus:ring-destructive-500",
  success: "bg-success-600 text-success-foreground hover:bg-success-700 focus:ring-success-500",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "min-h-11 min-w-11 pointer-fine:min-h-8 pointer-fine:min-w-8 px-2.5 py-1.5 text-sm",
  md: "min-h-11 min-w-11 pointer-fine:min-h-9 pointer-fine:min-w-9 px-3.5 py-2 text-sm",
};

export interface ButtonVariantOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

/**
 * Build the button class list for a given variant/size.
 * @param options - Variant, size and extra classes to merge
 * @param options.variant - Visual variant (default "primary")
 * @param options.size - Size token (default "md")
 * @param options.className - Extra classes merged last
 * @returns The merged class string
 */
export function buttonVariants({
  variant = "primary",
  size = "md",
  className,
}: ButtonVariantOptions = {}): string {
  return cn(baseStyles, variantStyles[variant], sizeStyles[size], className);
}

/**
 * Shared admin Button with token-based variants and size options.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      isLoading = false,
      disabled,
      type = "button",
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={buttonVariants({ variant, size, className })}
        disabled={disabled ?? isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading && (
          <Loader2
            className="-ml-0.5 mr-2 h-4 w-4 shrink-0 animate-spin"
            aria-hidden="true"
          />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
